import ChargeHelper from 'features/Charge/chargeHelper';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

DialogScheduledMaintenanceCabinetView.propTypes = {
  chargePointDetail: PropTypes.object,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};

function DialogScheduledMaintenanceCabinetView({ chargePointDetail, onCancel, onConfirm }) {
  const [readyToValidate, setReadyToValidate] = useState(false);

  const [selectedChargeStation, setSelectedChargeStation] = useState(null);
  const [selectedCabinet, setSelectedCabinet] = useState(
    Utils.isValid(chargePointDetail?.pointId)
      ? {
          id: chargePointDetail?.pointId,
          name: chargePointDetail?.pointCode,
          item: chargePointDetail,
        }
      : null
  );

  const [startDate, setStartDate] = useState('');
  const [cycleDays, setCycleDays] = useState('');
  const [description, setDescription] = useState('');
  const [applyForAllCabinets, setApplyForAllCabinets] = useState(true);

  useEffect(() => {
    const stationId = chargePointDetail?.stationId;
    if (!Utils.isValid(stationId)) return;
    ChargeHelper.API.getChargeStationDetail(stationId).then((station) => {
      if (!station) return;
      setSelectedChargeStation({
        id: station?.stationId,
        name: station?.name,
        item: station,
        address: ChargeHelper.Utils.getChargeStationFullAddress(station),
      });
    });
  }, [chargePointDetail]);

  useEffect(() => {
    // When station changes, clear cabinet selection if mismatch
    const currentStationId = selectedChargeStation?.id;
    const cabinetStationId = selectedCabinet?.item?.stationId;
    if (Utils.isValid(currentStationId) && Utils.isValid(cabinetStationId) && currentStationId !== cabinetStationId) {
      setSelectedCabinet(null);
    }
  }, [selectedChargeStation, selectedCabinet]);

  const isValidStation = Utils.isValid(selectedChargeStation);
  const isValidCabinet = Utils.isValid(selectedCabinet);
  const isValidStartDate = Utils.isValid(startDate);
  const isValidCycleDays = Utils.isValid(cycleDays) && Number(cycleDays) > 0;
  const isValidDescription = Utils.isValid(description);

  const handleSubmit = () => {
    setReadyToValidate(true);

    if (!isValidStation) {
      ToastHelper.showError('Vui lòng chọn trạm đổi pin');
      return;
    }
    if (!isValidStartDate) {
      ToastHelper.showError('Vui lòng chọn ngày bắt đầu');
      return;
    }
    if (!isValidCycleDays) {
      ToastHelper.showError('Vui lòng nhập chu kỳ (số ngày)');
      return;
    }
    if (!isValidDescription) {
      ToastHelper.showError('Vui lòng nhập mô tả');
      return;
    }

    if (onConfirm) {
      onConfirm({
        stationId: selectedChargeStation?.id,
        cabinetId: applyForAllCabinets ? null : selectedCabinet?.id,
        startDate,
        cycle: Number(cycleDays),
        description,
        applyToAllCabinets: applyForAllCabinets,
      });
    }
  };

  const dropdownChargeStationEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="scheduled-maintenance-station"
        name="scheduled-maintenance-station"
        disabled={true}
        toggleElement={
          <button
            className={`btn btn-white border-secondary w-100 d-flex align-items-center justify-content-between text-left ${
              readyToValidate && !isValidStation ? 'button-invalid' : ''
            }`}
            type="button"
            style={{ cursor: 'default', pointerEvents: 'none', backgroundColor: '#EEF1FA', minWidth: 0 }}
          >
            <span className="text-truncate" style={{ minWidth: 0 }}>
              {selectedChargeStation?.name ?? '[Tên trạm đổi pin hiện tại]'}
            </span>
          </button>
        }
        selectedValue={selectedChargeStation}
        subTitleType="address"
        dropdownMenuItems={[{ name: 'Trạm đổi pin', role: 'STATION' }]}
        getSubItems={async () => ({ values: [], total: 0 })}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={() => {}}
        isDropdownForm={true}
        defaultValue={{
          name: 'Trạm đổi pin',
          role: 'STATION',
        }}
      />
    );
  }, [readyToValidate, isValidStation, selectedChargeStation]);

  const dropdownCabinetEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="scheduled-maintenance-cabinet"
        name="scheduled-maintenance-cabinet"
        disabled={true}
        toggleElement={
          <button
            className={`btn btn-white border-secondary w-100 d-flex align-items-center justify-content-between text-left ${
              readyToValidate && !isValidCabinet ? 'button-invalid' : ''
            }`}
            type="button"
            style={{ cursor: 'default', pointerEvents: 'none', backgroundColor: '#EEF1FA', minWidth: 0 }}
          >
            <span className="text-truncate" style={{ minWidth: 0 }}>
              {selectedCabinet?.name ?? '[Mã tủ pin]'}
            </span>
          </button>
        }
        selectedValue={selectedCabinet}
        dropdownMenuItems={[{ name: 'Tủ pin', role: 'CABINET' }]}
        getSubItems={async () => ({ values: [], total: 0 })}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={() => {}}
        isDropdownForm={true}
        defaultValue={{
          name: 'Tủ pin',
          role: 'CABINET',
        }}
      />
    );
  }, [readyToValidate, isValidCabinet, selectedCabinet]);

  return (
    <div>
      <div className="p-3">
        <div className="row m-0">
          <div className="col-12 col-md-6 mb-3 px-0" style={{ minWidth: 0 }}>
            {UIHelper.buildFormLabel('Trạm đổi pin', true)}
            {dropdownChargeStationEl}
          </div>

          <div className="col-12 col-md-6 mb-3 px-0 pl-3" style={{ minWidth: 0 }}>
            {UIHelper.buildFormLabel('Tủ pin', true)}
            {dropdownCabinetEl}
          </div>

          <div className="col-12 col-md-6 mb-3 px-0">
            {UIHelper.buildFormLabel('Ngày bắt đầu', true)}
            <KTFormInput
              type={KTFormInputType.date}
              name="scheduled-maintenance-startDate"
              value={startDate}
              placeholder="DD/MM/YYYY"
              onChange={(value) => {
                setStartDate(value);
              }}
              enableCheckValid={true}
              isTouched={readyToValidate}
              isValid={isValidStartDate}
              feedbackText="Vui lòng chọn ngày bắt đầu"
              additionalInputClassName="border-secondary"
            />
          </div>

          <div className="col-12 col-md-6 mb-3 px-0 pl-3">
            {UIHelper.buildFormLabel('Chu kỳ', true)}
            <KTFormInput
              type={KTFormInputType.text}
              name="scheduled-maintenance-cycleDays"
              value={cycleDays}
              placeholder="Nhập số ngày"
              onChange={(value) => {
                if (_.isEmpty(value)) {
                  setCycleDays('');
                  return;
                }
                if (!/^\d+$/.test(value)) return;
                setCycleDays(value);
              }}
              enableCheckValid={true}
              isTouched={readyToValidate}
              isValid={isValidCycleDays}
              feedbackText="Vui lòng nhập chu kỳ"
              additionalInputClassName="border-secondary pr-15"
              unit="Ngày"
              showUnit={true}
            />
          </div>

          <div className="col-12 px-0 mb-3">
            {UIHelper.buildFormLabel('Mô tả', true)}
            <textarea
              className={`form-control border-secondary ${readyToValidate && !isValidDescription ? 'is-invalid' : ''}`}
              placeholder="Nhập mô tả"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e?.target?.value ?? '')}
            />
          </div>

          <div className="col-12 px-0">
            <label className="d-flex align-items-center m-0 cursor-pointer">
              <input
                type="checkbox"
                checked={applyForAllCabinets}
                onChange={(e) => setApplyForAllCabinets(!!e?.target?.checked)}
                style={{ width: 16, height: 16 }}
              />
              <span className="ml-3" style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                Áp dụng lịch này cho các tủ pin còn lại thuộc trạm đổi pin
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* footer */}
      <div
        className="d-flex align-items-center justify-content-end gap-3 p-3 w-100"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Bỏ qua', onCancel, null, true)}
        {UIHelper.buildGreenButton('Lưu thiết lập', handleSubmit, null, true)}
      </div>
    </div>
  );
}

export default DialogScheduledMaintenanceCabinetView;

